import { useEffect, useRef, useState } from 'react';
import { ref, onChildAdded, off, limitToLast, query, orderByKey, startAfter } from 'firebase/database';
import { database } from '../firebase/firebaseConfig';
import { showInfoToast } from '../shared/utils/toastHelper';
import { apiGet } from '../shared/utils/apiConfig';
import { playNotificationSound } from '../shared/utils/notificationSound';

export default function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const lastKeyRef = useRef(null);
  const unsubRef = useRef(null);
  const initializedRef = useRef(false);
  const lastToastTimeRef = useRef(0);
  const initTimestampRef = useRef(null);
  const lastFirebaseKeyRef = useRef(null);
  const processedKeysRef = useRef(new Set());
  const userTypeRef = useRef(null);

  // تحديد نوع المستخدم والـ Firebase path
  const getUserType = () => {
    const adminData = sessionStorage.getItem('adminkhadamat');
    const storeData = sessionStorage.getItem('storekhadamat');
    
    if (adminData) {
      try {
        const parsed = JSON.parse(adminData);
        return { 
          type: 'admin', 
          token: parsed.data?.token?.access_token || parsed.data?.access_token || parsed.token,
          id: parsed.data?.id || parsed.id
        };
      } catch {
        return null;
      }
    }
    
    if (storeData) {
      try {
        const parsed = JSON.parse(storeData);
        return { 
          type: parsed.role || 'store', 
          token: parsed.token,
          id: parsed.id
        };
      } catch {
        return null;
      }
    }
    
    return null;
  };

  // جلب الإشعارات القديمة من API
  const fetchOldNotifications = async (userType, token) => {
    try {
      let notificationType;
      if (userType === 'admin') {
        notificationType = 'dashboard';
      } else if (userType === 'pharmacy_branch') {
        notificationType = 'branch';
      } else if (userType === 'store') {
        notificationType = 'store';
      } else {
        notificationType = 'store'; 
      }
      
      const response = await apiGet(`/notifications?type=${notificationType}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
        }
      });

      if (response.data?.status === 'success' || response.data?.status === 200 || response.status === 200) {
        const oldNotifications = response.data?.data || response.data || [];
        
        // للأدمن: استخدام الإشعارات كما هي
        // للمستخدمين الآخرين: إضافة isNew: false للإشعارات القديمة
        const processedNotifications = userType === 'admin' 
          ? oldNotifications 
          : oldNotifications.map(notification => ({ 
              ...notification, 
              isNew: false // الإشعارات القديمة مقروءة
            }));
        
        setNotifications(processedNotifications);
        
        // للأدمن: تعيين العداد كما هو
        // للمستخدمين الآخرين: تعيين العداد بعدد الإشعارات القديمة
        if (userType === 'admin') {
          setUnreadCount(oldNotifications.length);
        } else {
          setUnreadCount(oldNotifications.length);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // الاستماع للإشعارات الجديدة من Firebase فقط
  const setupFirebaseListener = (userType, userId) => {
    let firebasePath;
    if (userType === 'admin') {
      firebasePath = 'dashboard_notifications';
    } else {
      // استخدام الـ id من sessionStorage لبناء المسار
      firebasePath = `branch_orders/${userId}`;
    }
    
    // استمع فقط للإشعار الأخير (الجديد)
    const notifsRef = query(ref(database, firebasePath), orderByKey(), limitToLast(1));

    unsubRef.current = onChildAdded(notifsRef, (snapshot) => {
      const data = snapshot.val();
      const key = snapshot.key;
      
      if (!data) return;

      // تجاهل الإشعار الأول (القديم) عند التهيئة
      if (!lastFirebaseKeyRef.current) {
        lastFirebaseKeyRef.current = key;
        processedKeysRef.current.add(key);
        return;
      }

      // تجاهل إذا كان نفس آخر key أو تم معالجته مسبقاً
      if (lastFirebaseKeyRef.current === key || processedKeysRef.current.has(key)) {
        return;
      }

      lastFirebaseKeyRef.current = key;
      processedKeysRef.current.add(key);

      setNotifications((prev) => {
        // تحقق من أن الإشعار غير موجود مسبقاً
        const exists = prev.some(n => n.id === key || n.id === String(key));
        
        if (exists) {
          return prev;
        }
        
        // تحويل بيانات Firebase إلى نفس هيكل API
        const normalizedData = {
          id: key,
          title_ar: data.title_ar || data.title || 'إشعار جديد',
          title_en: data.title_en || data.title || 'New Notification',
          content_ar: data.content_ar && !/^[a-zA-Z\s]+$/.test(data.content_ar) ? data.content_ar : '',
          content_en: data.content_en || data.message || data.body || '',
          created_at: data.created_at || new Date().toISOString(),
          isNew: true,
          client_id: data.client_id,
          pharmacy_branch_id: data.pharmacy_branch_id,
          request_id: data.request_id,
          notifiable_type: data.notifiable_type,
          notifiable_id: data.notifiable_id
        };
        
        // إظهار التوست قبل تحديث الحالة
        if (initializedRef.current) {
          const now = Date.now();
          const timeSinceLastToast = now - lastToastTimeRef.current;
          if (timeSinceLastToast > 5000) {
            const toastMessage = data.title_ar || data.title_en || data.message || 'إشعار جديد';
            showInfoToast(toastMessage);
            playNotificationSound();
            lastToastTimeRef.current = now;
          }
          
          // للأدمن: زيادة العداد مباشرة
          if (userTypeRef.current === 'admin') {
            setUnreadCount((c) => c + 1);
          }
        }
        
        // تحديث الحالة بشكل آمن - إزالة الحد الأقصى للإشعارات
        const next = [normalizedData, ...prev];
        return next;
      });
    });
  };

  useEffect(() => {
    const userInfo = getUserType();
    if (!userInfo) {
      setLoading(false);
      return;
    }

    // حفظ نوع المستخدم
    userTypeRef.current = userInfo.type;

    // حفظ وقت التهيئة
    initTimestampRef.current = Date.now();

    // جلب الإشعارات القديمة
    fetchOldNotifications(userInfo.type, userInfo.token);

    // إعداد الاستماع للإشعارات الجديدة
    setupFirebaseListener(userInfo.type, userInfo.id);

    return () => {
      if (unsubRef.current) {
        let firebasePath;
        if (userInfo.type === 'admin') {
          firebasePath = 'dashboard_notifications';
        } else {
          firebasePath = `branch_orders/${userInfo.id}`;
        }
        const notifsRef = ref(database, firebasePath);
        off(notifsRef, 'child_added', unsubRef.current);
        unsubRef.current = null;
      }
      // تنظيف البيانات المؤقتة
      processedKeysRef.current.clear();
      lastFirebaseKeyRef.current = null;
    };
  }, []);

  // تأخير تفعيل التوست لتجنب الإشعارات الأولية
  useEffect(() => {
    const timer = setTimeout(() => {
      initializedRef.current = true;
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // تحديث العداد تلقائياً عند تغيير عدد الإشعارات (للمستخدمين غير الأدمن فقط)
  useEffect(() => {
    if (userTypeRef.current !== 'admin') {
      // حساب جميع الإشعارات (القديمة + الجديدة)
      const totalCount = notifications.length;
      setUnreadCount(totalCount);
    }
  }, [notifications]);

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, isNew: false })));
    setUnreadCount(0);
  };

  return { 
    notifications, 
    unreadCount, 
    markAllRead, 
    loading 
  };
}